<template>
    <Dialog
        :value="true"
        :header-text="formTitle"
        fixed
        @close_modal="closeDialog"
    >
        <form>
            <v-row no-gutters>
                <v-col
                    cols="12"
                    sm="12"
                    md="12"
                >
                    <v-text-field
                        v-model="product_field.label"
                        outlined
                        :label="label_label"
                        :error-messages="product_field.label_error"
                        autofocus
                    />
                </v-col>
                <v-col
                    v-if="product_field.type === PRODUCT_FIELD_TYPES.BANK_ACCOUNT"
                    cols="12"
                    sm="12"
                    md="12"
                >
                    <v-text-field
                        v-model="product_field.value"
                        outlined
                        :error-messages="product_field.value_error"
                        :label="$t('IBAN')"
                    />
                </v-col>
                <v-col
                    cols="12"
                    sm="12"
                    md="12"
                >
                    <v-text-field
                        v-model="product_field.code"
                        outlined
                        :error-messages="product_field.code_error"
                        :label="code_label"
                    />
                </v-col>
                <v-col cols="12" sm="12" md="12">
                    <v-text-field v-model="externalId" outlined label="External ID"/>
                </v-col>
            </v-row>

            <template v-if="product_field.type === PRODUCT_FIELD_TYPES.RESOURCE">
                <v-row no-gutters class="pt-4">
                    <div class="font-weight-medium">
                        {{ $t('Members') }}
                    </div>

                    <v-col cols="12">
                        <ComboboxDropdown
                            :items="members"
                            :selected="members.filter(x => selectedMembers.includes(x.id))"
                            name-key="name"
                            value-key="id"
                            show-selected-first
                            multi
                            @input="onChangeSelectedMembers"
                        />
                    </v-col>

                    <v-col v-if="selectedMembers.length" cols="8">
                        <div class="align-center justify-space-between">
                            <v-row
                                v-for="member of members.filter(x => selectedMembers.includes(x.id))"
                                :key="member.id"
                                no-gutters
                            >
                                <v-col cols="4">
                                    <v-text-field
                                        :value="getMemberShare(member.id)"
                                        :disabled="isEqualShare"
                                        @keypress="filterNumber"
                                        @change="setMemberShare(member.id, $event)"
                                        class="input-align-right pr-2"
                                        hide-details
                                        single-line
                                        outlined
                                    />
                                </v-col>
                                <v-col cols="8" class="d-flex align-center vertical-align-middle pt-4">
                                    {{ member.name }}
                                </v-col>
                            </v-row>
                        </div>
                    </v-col>

                    <v-col v-if="selectedMembers.length" cols="4">
                        <v-row>
                            <v-col cols="12">
                                <div class="font-weight-medium pt-4 pl-4">
                                    {{ $t('Total') }} {{ totalMemberShares }}%
                                </div>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12">
                                <secondary-button @click="setEqualShareBetweenMembers">
                                    {{ $t('Share equally') }}
                                </secondary-button>
                            </v-col>
                        </v-row>
                    </v-col>

                </v-row>
            </template>

        </form>

        <template slot="footer">
            <v-btn
                v-if="product_field.id"
                text
                color="error"
                @click.stop="onArchiveClick"
            >
                {{ $t("Archive") }}
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
                text
                color="primary"
                @click="closeDialog"
            >
                {{ $t("Cancel") }}
            </v-btn>
            <v-btn
                color="primary"
                class="white--text"
                @click="onSaveClick"
            >
                {{ product_field.id ? $t("Save") : $t("Add") }}
            </v-btn>
        </template>
    </Dialog>
</template>

<script>
import Dialog from "@/components/Dialog.vue";
import {computed, reactive, toRefs, ref, watch, onMounted} from "@vue/composition-api";
import DialogModule from "../../store/modules/dialog";
import ProductFieldModule from "../../store/modules/product_field";
import AppModule from "../../store/modules/app";
import UserModule from "@/store/modules/user"
import {$t} from "@/i18n";
import {formatNumber, cleanNumber} from "@/plugins/formatting";
import {PRODUCT_FIELD_TYPES} from "../../helpers/constants";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import ComboboxDropdown from "../../components/ComboboxDropdown";
import AttachmentChip from "@/components/AttachmentChip.vue";
import {filterNumber} from "@/helpers/helpers";
import TemplateChooseDialog from "../../components/Emailing/EmailEdit/TemplateChooseDialog.vue";

export default {
    name: "EditProductFieldDialog",
    components: {
        TemplateChooseDialog,
        AttachmentChip,
        Dialog,
        ComboboxDropdown
    },
    props: {
        dialogIndex: {
            type: Number,
            default: null
        }
    },
    setup(props) {
        const userModule = UserModule()
        const {dialogs, closeDialog, setConfirmOptions, openConfirmDialog} = DialogModule();
        const {archiveProductField, saveProductField} = ProductFieldModule();
        const {clearValidationErrors, handleErrorResponse} = AppModule();
        const state = reactive({product_field: null});
        const dialogPayload = dialogs.value[props.dialogIndex].payload
        const selectedMembers = ref([])
        const externalId = ref(null)
        const isEqualShare = ref(false)

        const members = computed(() => {
            if (state.product_field && state.product_field.account_id) {
                return userModule.getVisiblePeopleByCompany(state.product_field.account_id)
            }

            return []
        })

        const totalMemberShares = computed(() => {
            if (!(state.product_field._data && state.product_field._data.member_shares)) {
                return 0
            }

            let total = 0

            for (const memberId of selectedMembers.value) {
                total += state.product_field._data.member_shares[memberId] || 0
            }

            return Math.round(total * 100) / 100
        })

        const getMemberShare = (memberId) => {
            if (!(state.product_field._data && state.product_field._data.member_shares)) {
                return 0
            }

            return state.product_field._data.member_shares[memberId] || 0
        }

        const setEqualShareBetweenMembers = () => {
            const sharePerMember = 100 / selectedMembers.value.length

            for (const memberId of selectedMembers.value) {
                setMemberShare(memberId, sharePerMember)
            }

            if (selectedMembers.value.length) {
                const remainingShare = 100 - totalMemberShares.value
                if (remainingShare) {
                    const memberShare = sharePerMember + remainingShare
                    setMemberShare(selectedMembers.value[0], memberShare)
                }
            }
        }

        const setMemberShare = (memberId, value) => {
            if (!state.product_field._data) {
                state.product_field._data = {}
            }

            let cleanedValue = Number(cleanNumber(value)) || 0
            cleanedValue = Math.round(cleanedValue * 10000) / 10000

            // Rewrite everything incase of removing a member then the value would still persist
            let memberShares = {}

            for (const memberId of selectedMembers.value) {
                memberShares[memberId] = getMemberShare(memberId)
            }

            memberShares[memberId] = cleanedValue
            state.product_field._data.member_shares = memberShares

            // Member shares is not reactive, ugly hack
            let data = {...state.product_field}
            state.product_field = data
        }

        const onChangeSelectedMembers = (value) => {
            selectedMembers.value = value.map(x => x.id)

            let memberShares = {}

            for (const memberId of selectedMembers.value) {
                memberShares[memberId] = getMemberShare(memberId)
            }

            state.product_field._data.member_shares = memberShares

            // Member shares is not reactive, ugly hack
            let data = {...state.product_field}
            state.product_field = data
        }

        watch(() => dialogPayload, () => {
            let target = {
                label: "",
                label_error: null,
                type: "",
                type_error: null,
                value: "",
                value_error: null,
                code: "",
                code_error: null
            }

            if (dialogPayload.type === 'resource') {
                target._data = {
                    member_shares: {}
                }
            }

            state.product_field = Object.assign(
                target,
                dialogPayload
            );

            if (state.product_field.type === PRODUCT_FIELD_TYPES.RESOURCE) {
                selectedMembers.value =
                    state.product_field._data &&
                    state.product_field._data.person_ids ? state.product_field._data.person_ids : []
            }
            if (state.product_field._data && state.product_field._data.id) {
                externalId.value = state.product_field._data.id
            }
        });

        const formTitle = computed(() => {
            let strings = {
                vat: $t("Tax"),
                unit: $t("Unit"),

                article: $t("Article"),
                resource: $t("Resource"),
                billing_project: $t("Project"),
                cost_center: $t("Cost center"),

                bank_account: $t("Bank account"),
            }

            let text = strings[state.product_field.type]

            if (state.product_field && state.product_field.source) {
                text += ' (' + state.product_field.source_text + ')'
            }

            return text
        });
        const code_label = computed(() => {
            if (state.product_field.type === PRODUCT_FIELD_TYPES.BANK_ACCOUNT) {
                return $t("SWIFT code");
            }
            return $t("Code");
        });
        const label_label = computed(() => {
            if (state.product_field.type === PRODUCT_FIELD_TYPES.BANK_ACCOUNT) {
                return $t("Bank name");
            }
            return $t("Description");
        });
        const onSaveClick = () => {
            clearValidationErrors(state.product_field);

            if (!state.product_field._data) {
                state.product_field._data = {}
            }

            if (state.product_field.type === PRODUCT_FIELD_TYPES.RESOURCE) {
                state.product_field._data.person_ids = selectedMembers.value
            }

            if (state.product_field._data.id) {
                state.product_field._data.id = externalId.value
            } else if (externalId.value) {
                state.product_field._data.id = externalId.value
            }

            saveProductField(state.product_field)
                .then(function () {
                    closeDialog();
                })
                .catch(function (ex) {
                    handleErrorResponse(state.product_field, ex);
                });
        };

        const archive = () => {
            archiveProductField(state.product_field)
                .then(function () {
                    closeDialog();
                })
                .catch(function (ex) {
                    handleErrorResponse(state.product_field, ex);
                });
        };
        const onArchiveClick = () => {
            setConfirmOptions({
                title: $t("Are you sure?"),
                confirm_text: $t("Confirm"),
                cancel_text: $t("Cancel"),
                action: archive
            });
            openConfirmDialog();
        };

        onMounted(async () => {
            try {
                await userModule.loadMembers()
            } catch (e) {
                errorDialog.showError(e)
            }
        })

        return {
            ...toRefs(state),
            formTitle,
            code_label,
            label_label,
            closeDialog,
            onSaveClick,
            onArchiveClick,
            PRODUCT_FIELD_TYPES,
            members,
            selectedMembers,
            externalId,
            isEqualShare,
            getMemberShare,
            setMemberShare,
            filterNumber,
            setEqualShareBetweenMembers,
            onChangeSelectedMembers,
            totalMemberShares
        };
    }
};
</script>
